import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NetDirection } from '../../vatMeta';

type VatSummaryProps = {
    /** `null` while the report is in flight — labels stay, values skeleton. */
    outputText: string | null;
    inputText: string | null;
    /** Magnitude of the net; `direction` carries the payable/reclaimable meaning. */
    netText: string | null;
    direction: NetDirection | null;
};

const Figure: React.FC<{
    label: string;
    /** `null` renders a skeleton in the value's place. */
    value: string | null;
    slot: string;
    emphasised?: boolean;
    children?: React.ReactNode;
}> = ({ label, value, slot, emphasised, children }) => (
    <div
        className={cn(
            'flex flex-col gap-1 rounded-xl bg-surface-secondary p-4',
            emphasised && 'gap-2 ring-1 ring-inset ring-accent/40',
        )}
    >
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        {value === null ? (
            <Skeleton
                className={cn(
                    'my-1 rounded',
                    emphasised ? 'h-9 w-36' : 'h-7 w-28',
                )}
            />
        ) : (
            <span
                className={cn(
                    'tabular-nums text-foreground',
                    emphasised
                        ? 'text-3xl font-bold'
                        : 'text-2xl font-semibold',
                )}
                {...getNodeTestIdProp(`vat-summary-${slot}`)}
            >
                {value}
            </span>
        )}
        {children}
    </div>
);

const DIRECTION_CHIP: Record<
    NetDirection,
    { labelKey: string; className: string }
> = {
    PAYABLE: {
        labelKey: 'vat.summary.payable',
        className: 'bg-warning-soft text-warning-soft-foreground',
    },
    RECLAIMABLE: {
        labelKey: 'vat.summary.reclaimable',
        className: 'bg-success-soft text-success-soft-foreground',
    },
    NIL: {
        labelKey: 'vat.summary.nothingToSettle',
        className: 'bg-surface text-muted',
    },
};

/**
 * The VAT report payoff: Output, Input, and the Net — the net emphasised, with
 * its direction spelled out (payable to / reclaimable from the budget) so the
 * meaning is unmistakable in words, not just the sign.
 */
export const VatSummary: React.FC<VatSummaryProps> = ({
    outputText,
    inputText,
    netText,
    direction,
}) => {
    const { t } = useTranslation('pages');
    // No direction until the report resolves — a payable/reclaimable chip shown
    // before the net is known would be a guess at the sign.
    const chip = direction === null ? null : DIRECTION_CHIP[direction];

    return (
        <div className="grid gap-4 sm:grid-cols-3">
            <Figure
                label={t('vat.summary.output')}
                slot="output"
                value={outputText}
            />
            <Figure
                label={t('vat.summary.input')}
                slot="input"
                value={inputText}
            />
            <Figure
                emphasised
                label={t('vat.summary.net')}
                slot="net"
                value={netText}
            >
                {chip === null ? (
                    <Skeleton className="h-6 w-32 rounded-lg" />
                ) : (
                    <span
                        className={cn(
                            'w-fit rounded-lg px-2.5 py-1 text-xs font-semibold',
                            chip.className,
                        )}
                        {...getNodeTestIdProp('vat-summary-direction')}
                    >
                        {t(chip.labelKey)}
                    </span>
                )}
            </Figure>
        </div>
    );
};
